import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import type { FinWorkflowDefinition } from '@/types/finance';

type StageRow = { order: number; name: string; roleCode: string };

const ENTITY_TYPES = [
  { value: 'JOURNAL_ENTRY', label: 'Journal entry' },
  { value: 'EXPENSE', label: 'Expense' },
  { value: 'PAYMENT_RUN', label: 'Payment run' },
  { value: 'INVOICE', label: 'Invoice' },
];

export function FinanceWorkflowDesignerPage() {
  const [workflows, setWorkflows] = useState<FinWorkflowDefinition[]>([]);
  const [workflowCode, setWorkflowCode] = useState('');
  const [workflowName, setWorkflowName] = useState('');
  const [entityType, setEntityType] = useState('JOURNAL_ENTRY');
  const [routingType, setRoutingType] = useState('SEQUENTIAL');
  const [stages, setStages] = useState<StageRow[]>([
    { order: 1, name: 'Manager review', roleCode: 'FINANCE_APPROVE' },
  ]);
  const [stageName, setStageName] = useState('');
  const [stageRole, setStageRole] = useState('FINANCE_APPROVE');
  const [saving, setSaving] = useState(false);
  const { rows: paginatedStages, tablePaginationProps: stagesPaginationProps } = useClientPaginatedRows(stages);

  const load = useCallback(() => {
    financeApi.listWorkflows().then(setWorkflows).catch((e) => showApiError(e, 'Failed to load workflows'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addStage = () => {
    if (!stageName.trim()) return;
    setStages((prev) => [
      ...prev,
      { order: prev.length + 1, name: stageName.trim(), roleCode: stageRole.trim() || 'FINANCE_APPROVE' },
    ]);
    setStageName('');
  };

  const removeStage = (order: number) => {
    setStages((prev) =>
      prev.filter((s) => s.order !== order).map((s, i) => ({ ...s, order: i + 1 })),
    );
  };

  const saveWorkflow = async (e: FormEvent) => {
    e.preventDefault();
    if (!workflowCode.trim() || !workflowName.trim() || stages.length === 0) {
      notify.error('Code, name, and at least one stage are required');
      return;
    }
    setSaving(true);
    try {
      await financeApi.saveWorkflow({
        workflowCode: workflowCode.trim(),
        workflowName: workflowName.trim(),
        entityType,
        routingType,
        stagesJson: {
          stages: stages.map((s) => ({
            order: s.order,
            name: s.name,
            roleCode: s.roleCode,
          })),
        },
      });
      notify.success('Workflow saved');
      setWorkflowCode('');
      setWorkflowName('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Workflow designer"
        description="Configure multi-stage approval workflows for finance documents"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Workflows', to: '/finance/workflows' },
          { label: 'Designer' },
        ]}
        actions={
          <ButtonLink to="/finance/workflows" variant="secondary" size="sm">
            Back to workflows
          </ButtonLink>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card p-4 space-y-3">
          <h3 className="font-semibold">Existing workflows</h3>
          <ul className="text-sm space-y-2">
            {workflows.map((w) => (
              <li key={w.id} className="rounded border p-2">
                <div className="font-medium">{w.workflowName}</div>
                <div className="text-xs text-slate-500">
                  {w.workflowCode} · {w.entityType} · {w.routingType}
                </div>
              </li>
            ))}
            {workflows.length === 0 && <li className="text-slate-500">No workflows yet.</li>}
          </ul>
        </div>

        <div className="card p-4 lg:col-span-2 space-y-4">
          <h3 className="font-semibold">Create / update workflow</h3>
          <form onSubmit={saveWorkflow} className="grid gap-3 sm:grid-cols-2">
            <Input label="Workflow code" value={workflowCode} onChange={(e) => setWorkflowCode(e.target.value)} />
            <Input label="Workflow name" value={workflowName} onChange={(e) => setWorkflowName(e.target.value)} />
            <Select
              label="Entity type"
              options={ENTITY_TYPES}
              value={entityType}
              onChange={setEntityType}
            />
            <Select
              label="Routing"
              options={[
                { value: 'SEQUENTIAL', label: 'Sequential' },
                { value: 'PARALLEL', label: 'Parallel' },
              ]}
              value={routingType}
              onChange={setRoutingType}
            />
          </form>

          <div className="border-t pt-4 space-y-3">
            <h4 className="font-medium text-sm">Approval stages</h4>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Stage</th>
                  <th className="p-2 text-left">Role</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {paginatedStages.map((s) => (
                  <tr key={s.order} className="border-t">
                    <td className="p-2">{s.order}</td>
                    <td className="p-2">{s.name}</td>
                    <td className="p-2">{s.roleCode}</td>
                    <td className="p-2 text-right">
                      <button type="button" className="text-xs text-red-600" onClick={() => removeStage(s.order)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination {...stagesPaginationProps} hasRows={stages.length > 0} />
            <div className="grid gap-3 sm:grid-cols-3 items-end">
              <Input label="Stage name" value={stageName} onChange={(e) => setStageName(e.target.value)} />
              <Input label="Approver role" value={stageRole} onChange={(e) => setStageRole(e.target.value)} />
              <Button type="button" variant="secondary" onClick={addStage}>
                Add stage
              </Button>
            </div>
            <Button type="button" loading={saving} onClick={(e) => void saveWorkflow(e as unknown as FormEvent)}>
              Save workflow
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
